import { css } from '@emotion/react';

import * as React from 'react';

import { EnterpriseBadge_UserState as EnterpriseBadgeUserState } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import EnterpriseLearningPathEnrollButtonNotConfigurable from 'bundles/enterprise-learning-path-description-page/components/EnterpriseLearningPathEnrollButtonNotConfigurable';
import { useEnterpriseLearningPathPageData } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import { useEnterpriseLearningPathCurriculum } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathCurriculumContext';
import {
  getEnrollingHeroActionId,
  shouldDisableActionButton,
  useSkillPathwayActivelyEnrolling,
} from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';
import { useGetBadgeUrl } from 'bundles/enterprise-learning-path-description-page/module-renderer/data/useGetBadgeUrl';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageQuery';
import { findActiveEnrolledCourseId } from 'bundles/enterprise-learning-path-description-page/utils/findActiveEnrolledCourseId';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  button: css`
    min-width: 190px;
  `,
};

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment | null;
};

const EnterpriseSkillPathwayEnrollButtonNotConfigurable = ({ pageData }: Props) => {
  const { programSlug } = useParams();
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const track = useTracker();
  const badgeUrl = useGetBadgeUrl(
    pageData?.enterpriseBadge?.id ?? '',
    pageData?.enterpriseBadge?.previewUrl ?? '',
    pageData?.userState === 'ISSUED'
  );
  const { curriculumProducts } = useEnterpriseLearningPathCurriculum();
  const learningPathPageData = useEnterpriseLearningPathPageData();
  const { currentEnrollingAction, setCurrentEnrollingAction } = useSkillPathwayActivelyEnrolling();

  // Find the active course slug for enrolled users
  const activeCourseSlug = React.useMemo(() => {
    // Only return course slug if user is enrolled
    if (!pageData || pageData.userState !== EnterpriseBadgeUserState.Enrolled) {
      return null;
    }

    const activeCourseId = findActiveEnrolledCourseId(curriculumProducts);

    if (!activeCourseId || !learningPathPageData?.enterpriseBadge?.badgeActivities) {
      return null;
    }

    // Find the matching course in badgeActivities to get the slug
    const matchingActivity = learningPathPageData.enterpriseBadge.badgeActivities.find(
      (activity) => activity.id === activeCourseId
    );

    return matchingActivity?.slug || null;
  }, [pageData, curriculumProducts, learningPathPageData?.enterpriseBadge?.badgeActivities]);

  const trackClick = () => {
    track('click_button', {
      button: {
        name: 'skill_pathway_hero_cta',
      },
      enterprise: {
        ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
        ...(program?.metadata?.slug ? { programSlug: program?.metadata?.slug } : {}),
      },
      product: {
        id: pageData?.enterpriseBadge?.id ?? '',
        type: 'learning_path',
      },
    });
  };

  if (!pageData) {
    return null;
  }

  if (pageData.userState === EnterpriseBadgeUserState.Enrolled) {
    return (
      <Button
        style={{ minWidth: '190px' }}
        onClick={() => {
          trackClick();

          window.open(
            activeCourseSlug
              ? `/learn/${activeCourseSlug}/home/welcome`
              : `/programs/${programSlug}/my-learning?myLearningTab=IN_PROGRESS`,
            '_self'
          );
        }}
      >
        {_t('Go to course')}
      </Button>
    );
  }

  if (
    pageData.userState === EnterpriseBadgeUserState.NeverEnrolled ||
    pageData.userState === EnterpriseBadgeUserState.Unenrolled
  ) {
    return (
      <EnterpriseLearningPathEnrollButtonNotConfigurable
        enrollStateText={_t('Start learning')}
        redirectToCourseHomeOnEnrollSuccess
        enrollButtonStylesOverride={styles.button}
        disabled={shouldDisableActionButton(getEnrollingHeroActionId(), currentEnrollingAction)}
        setCurrentEnrollingAction={setCurrentEnrollingAction}
        additionalTrackingEvent={trackClick}
        buttonDataTestId="skill-pathway-enroll-button"
        isSkillPathway
      />
    );
  }

  if (pageData.userState === EnterpriseBadgeUserState.Issued) {
    return (
      <Button
        style={{ minWidth: '190px' }}
        onClick={() => {
          trackClick();

          window.open(badgeUrl, '_self');
        }}
      >
        {_t('View Badge')}
      </Button>
    );
  }

  return null;
};

export default EnterpriseSkillPathwayEnrollButtonNotConfigurable;
